import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrganization } from "@/hooks/useOrganization";
import { useCustomerGroups } from "@/hooks/useCustomerGroups";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_COLORS = ["#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#6366F1", "#14B8A6"];

interface GroupMember {
  id: string;
  customer_id: string;
  customer: { full_name: string | null; email: string } | null;
}

export default function AdminCustomerGroups() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { groups, isLoading, createGroup, updateGroup, deleteGroup } = useCustomerGroups(organization?.id);
  const qc = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState<(typeof groups)[0] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<(typeof groups)[0] | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formColor, setFormColor] = useState(DEFAULT_COLORS[0]);
  const [canBookWithoutPayment, setCanBookWithoutPayment] = useState(false);

  // Fetch members for expanded group
  const { data: members = [] } = useQuery({
    queryKey: ["customer-group-members-list", expandedGroup],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_group_members" as any)
        .select("id, customer_id, customers(full_name, email)")
        .eq("customer_group_id", expandedGroup!);
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        customer_id: r.customer_id,
        customer: r.customers,
      })) as GroupMember[];
    },
    enabled: !!expandedGroup,
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase.from("customer_group_members" as any).delete().eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer-group-members-list", expandedGroup] });
      qc.invalidateQueries({ queryKey: ["customer-groups"] });
    },
    onError: () => toast.error("Kunde inte ta bort medlem"),
  });

  const openCreate = () => {
    setFormName("");
    setFormDesc("");
    setFormColor(DEFAULT_COLORS[0]);
    setCanBookWithoutPayment(false);
    setShowCreate(true);
  };

  const openEdit = (g: (typeof groups)[0]) => {
    setFormName(g.name);
    setFormDesc(g.description ?? "");
    setFormColor(g.color ?? DEFAULT_COLORS[0]);
    setCanBookWithoutPayment((g as any).can_book_without_payment ?? false);
    setEditGroup(g);
  };

  const handleCreate = () => {
    createGroup.mutate(
      { name: formName, description: formDesc, color: formColor, can_book_without_payment: canBookWithoutPayment },
      { onSuccess: () => setShowCreate(false) },
    );
  };

  const handleUpdate = () => {
    if (!editGroup) return;
    updateGroup.mutate(
      { id: editGroup.id, name: formName, description: formDesc, color: formColor, can_book_without_payment: canBookWithoutPayment },
      { onSuccess: () => setEditGroup(null) },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteGroup.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t("customerGroups.title")}</h2>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" /> {t("customerGroups.createGroup")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{t("customerGroups.noGroups")}</p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <Card key={g.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <span
                    className="h-4 w-4 rounded-full shrink-0"
                    style={{ backgroundColor: g.color ?? "#6366f1" }}
                  />
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => setExpandedGroup(expandedGroup === g.id ? null : g.id)}
                  >
                    <p className="font-medium text-sm">{g.name}</p>
                    {g.description && (
                      <p className="text-xs text-muted-foreground truncate">{g.description}</p>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    {g.member_count}
                  </Badge>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(g)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(g)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Expanded: member list */}
                {expandedGroup === g.id && (
                  <div className="mt-3 pt-3 border-t space-y-1">
                    {members.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{t("customerGroups.noMembers")}</p>
                    ) : (
                      members.map((m) => (
                        <div key={m.id} className="flex items-center justify-between text-sm py-1">
                          <span>{m.customer?.full_name ?? m.customer?.email ?? "—"}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive"
                            onClick={() => removeMember.mutate(m.id)}
                            disabled={removeMember.isPending}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showCreate || !!editGroup} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditGroup(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editGroup ? t("customerGroups.editGroup") : t("customerGroups.createGroup")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("customerGroups.name")}</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customerGroups.description")}</Label>
              <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="h-16" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customerGroups.color")}</Label>
              <div className="flex gap-2 flex-wrap">
                {DEFAULT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`h-7 w-7 rounded-full border-2 transition-all ${formColor === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setFormColor(c)}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>{t("customerGroups.canBookWithoutPayment")}</Label>
                <p className="text-xs text-muted-foreground">{t("customerGroups.canBookWithoutPaymentDesc")}</p>
              </div>
              <Switch checked={canBookWithoutPayment} onCheckedChange={setCanBookWithoutPayment} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditGroup(null); }}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={editGroup ? handleUpdate : handleCreate}
              disabled={!formName.trim() || createGroup.isPending || updateGroup.isPending}
            >
              {(createGroup.isPending || updateGroup.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editGroup ? t("common.save") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("customerGroups.deleteGroup")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("customerGroups.deleteConfirm", { name: deleteTarget?.name, count: deleteTarget?.member_count ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("customerGroups.deleteGroup")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
